import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const emptyLine = () => ({ accountId: '', type: 'DEBIT', amount: '' });
const empty = () => ({ description: '', frequency: 'MONTHLY', nextRun: '' });

export function RecurringJournalModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [lines, setLines] = useState([{ accountId: '', type: 'DEBIT', amount: '' }, { accountId: '', type: 'CREDIT', amount: '' }]);
  const [error, setError] = useState(null);

  const accounts = useQuery({ queryKey: ['finance', 'accounts'], queryFn: () => financeApi.listAccounts().then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setLines([{ accountId: '', type: 'DEBIT', amount: '' }, { accountId: '', type: 'CREDIT', amount: '' }]); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setLine = (i, k) => (e) => setLines((arr) => arr.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));

  const debit = lines.filter((l) => l.type === 'DEBIT').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const credit = lines.filter((l) => l.type === 'CREDIT').reduce((s, l) => s + (Number(l.amount) || 0), 0);
  const balanced = debit > 0 && Math.abs(debit - credit) < 0.005;

  const save = useMutation({
    mutationFn: (body) => financeApi.createRecurringJournal(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['finance', 'recurring-journals'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.description.trim() || !form.nextRun) { setError(t('rj.error.required')); return; }
    if (!balanced) { setError(t('rj.error.unbalanced')); return; }
    const clean = lines.filter((l) => l.accountId && Number(l.amount) > 0).map((l) => ({ accountId: l.accountId, type: l.type, amount: Number(l.amount) }));
    save.mutate({ description: form.description.trim(), frequency: form.frequency, nextRun: form.nextRun, lines: clean });
  }

  return (
    <Modal open={open} onClose={onClose} title={t('rj.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <label className="field"><span className="field-label">{t('rj.col.description')}</span>
          <input className="input" value={form.description} onChange={set('description')} required />
        </label>
        <div className="field-row">
          <label className="field" style={{ maxWidth: 180 }}><span className="field-label">{t('rj.col.frequency')}</span>
            <select className="input" value={form.frequency} onChange={set('frequency')}>
              <option value="MONTHLY">{t('rj.freq.MONTHLY')}</option>
              <option value="QUARTERLY">{t('rj.freq.QUARTERLY')}</option>
              <option value="YEARLY">{t('rj.freq.YEARLY')}</option>
            </select>
          </label>
          <label className="field" style={{ maxWidth: 180 }}><span className="field-label">{t('rj.field.nextRun')}</span>
            <input className="input" type="date" value={form.nextRun} onChange={set('nextRun')} required />
          </label>
        </div>

        <div className="field">
          <span className="field-label">{t('rj.field.lines')}</span>
          {lines.map((l, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
              <select className="input" value={l.accountId} onChange={setLine(i, 'accountId')}>
                <option value="">-</option>
                {(accounts.data || []).map((a) => <option key={a.id} value={a.id}>{a.code} · {a.name}</option>)}
              </select>
              <select className="input" style={{ maxWidth: 120 }} value={l.type} onChange={setLine(i, 'type')}>
                <option value="DEBIT">{t('rj.debit')}</option>
                <option value="CREDIT">{t('rj.credit')}</option>
              </select>
              <input className="input num" style={{ maxWidth: 130 }} type="number" min="0" step="0.01" placeholder={t('col.amount')} value={l.amount} onChange={setLine(i, 'amount')} />
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" onClick={() => setLines((a) => [...a, emptyLine()])}>{t('rj.field.addLine')}</Button>
          <span className="muted text-sm" style={{ marginLeft: 'var(--space-3)', color: balanced ? 'var(--color-success, #059669)' : 'var(--color-danger, #dc2626)' }}>
            {t('rj.debit')} {debit.toFixed(2)} / {t('rj.credit')} {credit.toFixed(2)} {balanced ? '✓' : '⚠'}
          </span>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
